import { ThemeType } from "@hex/common";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { styled } from "styled-components";

import { Heading } from "../../components/Heading";
import { TickSmallIcon } from "../../components/icons/CustomIcons";
import { Flex, HexButton, HexInputGroup, Txt } from "../../hex-components";

import { CliAuthWorkspaceSelect } from "./CliAuthWorkspaceSelect";

export namespace CliAuthFlowScreen {
  export const ConfirmUserCode = React.memo(function ConfirmCode({
    onAuthorize,
    userCode: providedUserCode,
  }: {
    userCode?: string | null;
    onAuthorize: (userCode: string, approve: boolean) => Promise<void>;
  }) {
    const [isPending, setIsPending] = useState(false);
    const [userCode, setUserCode] = useState(providedUserCode ?? "");
    useEffect(() => {
      if (providedUserCode) {
        setUserCode(providedUserCode);
      }
    }, [providedUserCode]);

    const onAction = useCallback(
      async (isApprove: boolean) => {
        setIsPending(true);
        try {
          await onAuthorize(userCode, isApprove);
        } finally {
          setIsPending(false);
        }
      },
      [onAuthorize, userCode],
    );

    return (
      <>
        <Heading renderAs="h1">Authorize Hex CLI</Heading>
        <Flex
          align="stretch"
          direction="column"
          gap={8}
          style={{ marginTop: 8 }}
        >
          <Txt>The Hex CLI is requesting access to your workspace:</Txt>

          <CliAuthWorkspaceSelect />
        </Flex>

        {providedUserCode ? (
          <Flex align="stretch" direction="column" gap={8}>
            <Txt>Confirm this code matches your terminal:</Txt>
            <UserCodeDisplay>{userCode}</UserCodeDisplay>
          </Flex>
        ) : (
          <>
            <Txt>Enter the code from your terminal:</Txt>
            <UserCodeInput
              autoFocus={true}
              disabled={isPending}
              id="user-code-input"
              large={true}
              placeholder="XXXX-XXXX"
              value={userCode}
              // eslint-disable-next-line react/jsx-no-bind -- ok
              onChange={(e) => setUserCode(e.target.value.toUpperCase())}
            />
          </>
        )}

        <Flex gap={8} justify="end" style={{ marginTop: 16 }}>
          <HexButton
            disabled={isPending}
            intent="danger"
            // eslint-disable-next-line react/jsx-no-bind -- ok
            onClick={() => onAction(false)}
          >
            Deny
          </HexButton>
          <HexButton
            disabled={isPending}
            icon={<TickSmallIcon />}
            intent="success"
            // eslint-disable-next-line react/jsx-no-bind -- ok
            onClick={() => onAction(true)}
          >
            Confirm
          </HexButton>
        </Flex>
      </>
    );
  });

  export const Authorized = React.memo(function Approved() {
    return (
      <>
        <Heading renderAs="h1">Authorized</Heading>
        <Txt>
          You have successfully authorized the Hex CLI. You can close this
          window and return to your terminal.
        </Txt>
      </>
    );
  });

  export const NotAuthorized = React.memo(function Denied({
    error = false,
    message,
  }: {
    message: ReactNode | string;
    error?: boolean;
  }) {
    const messageNode =
      typeof message === "string" ? <Txt>{message}</Txt> : message;
    return (
      <>
        <Heading renderAs="h1">
          Authorization {error ? "could not be completed" : "denied"}
        </Heading>
        {messageNode}
      </>
    );
  });
}

const UserCodeDisplay = styled.div`
  padding: 30px 24px 32px;
  border-radius: ${({ theme }) => theme.borderRadius};
  background: ${({ theme }) =>
    theme.type === ThemeType.DARK
      ? theme.backgroundColor.DEFAULT
      : theme.backgroundColor.MUTED};
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  text-align: center;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: 1px;
`;

const UserCodeInput = styled(HexInputGroup)`
  input {
    font-family: ${({ theme }) => theme.fontFamily.MONO};
    font-size: 24px;
    font-weight: 400;
    letter-spacing: 1px;
  }
`;
